// src/utils/push.js
//
// Web push (Firebase Cloud Messaging) helpers. Registers the device, stores its FCM token on the
// user's Firestore doc (users/{uid}.fcmTokens), and shows foreground notifications while the tab
// is open. Background notifications are handled by public/firebase-messaging-sw.js.
//
// Requires REACT_APP_FIREBASE_VAPID_KEY (Web Push certificate key pair from
// Firebase Console → Project Settings → Cloud Messaging → Web configuration).

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { app, db } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Basic capability check (browser + config).
export async function pushSupported() {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) return false;
    return await isSupported();
  } catch {
    return false;
  }
}

// Registers/refreshes this device's push token and saves it to the user's doc.
// Returns { ok, reason }. Never throws.
export async function registerPush(uid, { requestPermission = false } = {}) {
  try {
    if (!uid) return { ok: false, reason: 'no-user' };
    if (!VAPID_KEY) return { ok: false, reason: 'no-vapid' };
    if (!(await pushSupported())) return { ok: false, reason: 'unsupported' };

    let permission = Notification.permission;
    if (permission === 'default' && requestPermission) {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') return { ok: false, reason: permission === 'denied' ? 'denied' : 'not-granted' };

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return { ok: false, reason: 'no-token' };

    await setDoc(
      doc(db, 'users', uid),
      { fcmTokens: arrayUnion(token), fcmUpdatedAt: serverTimestamp() },
      { merge: true }
    );
    return { ok: true, token };
  } catch (err) {
    console.warn('[push] registerPush failed:', err?.message || err);
    return { ok: false, reason: 'error' };
  }
}

// Fires a real device notification right after the user enables notifications, so they get an
// immediate, tangible confirmation ("welcome" push) that everything is working. Uses the service
// worker's showNotification (works when installed as a PWA too) and falls back to Notification().
export async function showEnabledNotification() {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const title = 'تم تفعيل الإشعارات بنجاح ✅';
    const options = {
      body: 'رح توصلك المهام والتحديثات الجديدة على هذا الجهاز مباشرة.',
      icon: '/logo192.png',
      badge: '/logo192.png',
      dir: 'rtl',
      lang: 'ar',
      tag: 'push-enabled-welcome',
      data: { url: '/dashboard' },
    };
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (registration?.showNotification) {
        await registration.showNotification(title, options);
        return;
      }
    }
    // Fallback: page-context notification.
    const n = new Notification(title, options);
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) {
    console.warn('[push] showEnabledNotification failed:', err?.message || err);
  }
}

// Shows a notification for messages received while the app is in the foreground.
let foregroundBound = false;
export async function listenForegroundMessages() {
  try {
    if (foregroundBound) return;
    if (!(await pushSupported())) return;
    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
      const d = payload?.data || payload?.notification || {};
      const title = d.title || 'إشعار جديد';
      if (Notification.permission === 'granted') {
        const n = new Notification(title, {
          body: d.body || '',
          icon: '/logo192.png',
          dir: 'rtl',
          data: { url: d.url || '/' },
        });
        n.onclick = () => {
          window.focus();
          if (d.url) window.location.href = d.url;
          n.close();
        };
      }
    });
    foregroundBound = true;
  } catch (err) {
    console.warn('[push] listenForegroundMessages failed:', err?.message || err);
  }
}
